export type BoatCleatKey =
  | "bow"
  | "midship_port"
  | "midship_starboard"
  | "stern_port"
  | "stern_starboard";

interface BoatCleatSource {
  namedAnchors?: Record<string, [number, number, number]>;
}

export interface BoatCleatMarkerDiagnostic {
  key: BoatCleatKey;
  label: string;
  requiredNames: readonly string[];
  foundName?: string;
  position?: readonly [number, number, number];
}

export interface BoatCleatPoint {
  key: BoatCleatKey;
  label: string;
  name: string;
  position: readonly [number, number, number];
}

const BOAT_CLEAT_MARKERS: ReadonlyArray<{
  key: BoatCleatKey;
  label: string;
  names: readonly string[];
}> = [
  {
    key: "bow",
    label: "knaga dziobowa",
    names: ["Cleat.Bow", "Cleat.Bow.Center", "Mooring.Cleat.Bow"],
  },
  {
    key: "midship_port",
    label: "knaga śródokrętowa lewa",
    names: [
      "Cleat.Midship.Port",
      "Cleat.Mid.Port",
      "Cleat.Port.Midship",
      "Cleat.Port.Mid",
      "Cleat.Midship.L",
      "Cleat.Mid.L",
      "Mooring.Cleat.Midship.Port",
    ],
  },
  {
    key: "midship_starboard",
    label: "knaga śródokrętowa prawa",
    names: [
      "Cleat.Midship.Starboard",
      "Cleat.Mid.Starboard",
      "Cleat.Starboard.Midship",
      "Cleat.Starboard.Mid",
      "Cleat.Midship.R",
      "Cleat.Mid.R",
      "Mooring.Cleat.Midship.Starboard",
    ],
  },
  {
    key: "stern_port",
    label: "knaga rufowa lewa",
    names: [
      "Cleat.Stern.Port",
      "Cleat.Aft.Port",
      "Cleat.Port.Stern",
      "Cleat.Port.Aft",
      "Cleat.Stern.L",
      "Cleat.Aft.L",
      "Mooring.Cleat.Stern.Port",
    ],
  },
  {
    key: "stern_starboard",
    label: "knaga rufowa prawa",
    names: [
      "Cleat.Stern.Starboard",
      "Cleat.Aft.Starboard",
      "Cleat.Starboard.Stern",
      "Cleat.Starboard.Aft",
      "Cleat.Stern.R",
      "Cleat.Aft.R",
      "Mooring.Cleat.Stern.Starboard",
    ],
  },
];

const BOAT_CLEAT_KEYS = new Set<BoatCleatKey>(
  BOAT_CLEAT_MARKERS.map((marker) => marker.key),
);

export function isBoatCleatKey(value: string): value is BoatCleatKey {
  return BOAT_CLEAT_KEYS.has(value as BoatCleatKey);
}

export function getBoatCleatMarkerDiagnostics(
  source: BoatCleatSource,
): BoatCleatMarkerDiagnostic[] {
  const anchors = source.namedAnchors;
  return BOAT_CLEAT_MARKERS.map((marker) => {
    const found = anchors ? findAnchor(anchors, marker.names) : null;
    return {
      key: marker.key,
      label: marker.label,
      requiredNames: marker.names,
      foundName: found?.name,
      position: found?.position,
    };
  });
}

export function missingBoatCleatMarkers(
  source: BoatCleatSource,
): BoatCleatMarkerDiagnostic[] {
  return getBoatCleatMarkerDiagnostics(source).filter(
    (marker) => !marker.foundName,
  );
}

export function assertBoatCleatMarkers(source: BoatCleatSource): void {
  const missing = missingBoatCleatMarkers(source);
  if (missing.length === 0) return;
  const details = missing
    .map(
      (marker) =>
        `${marker.label}: add one of ${marker.requiredNames.join(" / ")}`,
    )
    .join("; ");
  throw new Error(`Missing player boat cleat markers: ${details}`);
}

export function getBoatCleatsFromAnchors(
  source: BoatCleatSource,
  options: { requireMarkers?: boolean } = {},
): BoatCleatPoint[] {
  if (options.requireMarkers) assertBoatCleatMarkers(source);
  return getBoatCleatMarkerDiagnostics(source)
    .filter(
      (marker): marker is BoatCleatMarkerDiagnostic & {
        foundName: string;
        position: readonly [number, number, number];
      } => !!marker.foundName && !!marker.position,
    )
    .map((marker) => ({
      key: marker.key,
      label: marker.label,
      name: marker.foundName,
      position: marker.position,
    }));
}

export function getBoatCleatPosition(
  source: BoatCleatSource,
  key: BoatCleatKey,
  options: { requireMarker?: boolean } = {},
): readonly [number, number, number] | null {
  const marker = getBoatCleatMarkerDiagnostics(source).find(
    (diagnostic) => diagnostic.key === key,
  );
  if (marker?.position) return marker.position;
  if (options.requireMarker) {
    const names = marker?.requiredNames.join(" / ") ?? key;
    throw new Error(
      `Missing player boat cleat marker ${key}: add one of ${names}`,
    );
  }
  return null;
}

function findAnchor(
  anchors: Readonly<Record<string, [number, number, number]>>,
  names: readonly string[],
): { name: string; position: [number, number, number] } | null {
  const wanted = new Set(names.map(normalizeAnchorName));
  for (const [name, pos] of Object.entries(anchors)) {
    if (wanted.has(normalizeAnchorName(name))) {
      return { name, position: pos };
    }
  }
  return null;
}

function normalizeAnchorName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[._\-\s]+/g, ".");
}